import React, { useState } from 'react';
import { Box, Paper, Typography, Button, Tabs, Tab, TextField, IconButton, Select, MenuItem, Chip, Switch, Checkbox, FormControlLabel, CircularProgress } from '@mui/material';
import { Delete, Add, DragIndicator, Refresh } from '@mui/icons-material';
import { proposalService } from '../services/api';

export default function StepConfig({ session, setSession, onNext, onBack }) {
  const [config, setConfig] = useState(session.config || session.analysis?.config || {});
  const [tab, setTab] = useState(0);

  const sections = config.proposal_sections || [];
  const diagrams = config.diagrams || [];
  const rules = config.generation_rules || [];
  const cc = config.cost_estimation_config || {};

  const updateSection = (i, field, val) => {
    const u = { ...config, proposal_sections: [...sections] };
    u.proposal_sections[i] = { ...u.proposal_sections[i], [field]: val };
    setConfig(u);
  };
  const removeSection = (i) => setConfig({ ...config, proposal_sections: sections.filter((_, j) => j !== i) });
  const addSection = () => setConfig({ ...config, proposal_sections: [...sections, { id: `custom_${Date.now()}`, title: 'New Section', instruction: '', include: true }] });

  const updateDiagram = (i, field, val) => {
    const u = [...diagrams]; u[i] = { ...u[i], [field]: val };
    setConfig({ ...config, diagrams: u });
  };
  const removeDiagram = (i) => setConfig({ ...config, diagrams: diagrams.filter((_, j) => j !== i) });
  const addDiagram = () => setConfig({ ...config, diagrams: [...diagrams, { title: '', section_id: sections[0]?.id || '', diagram_type: 'architecture', description: '' }] });

  return (
    <Paper sx={{ border: '1px solid #E2E8F0', overflow: 'hidden' }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: '1px solid #E2E8F0' }}>
        <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>Sections <Chip label={sections.filter(s => s.include !== false).length} size="small" color="primary" sx={{ height: 20 }} /></Box>} />
        <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>Diagrams <Chip label={diagrams.length} size="small" color="secondary" sx={{ height: 20 }} /></Box>} />
        <Tab label="Rules" />
        <Tab label="Cost" />
        <Tab label="JSON" />
      </Tabs>

      <Box sx={{ p: 3, maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}>
        {/* Sections */}
        {tab === 0 && (
          <Box>
            {sections.map((s, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 2.5, mb: 1.5, opacity: s.include !== false ? 1 : 0.5, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <DragIndicator sx={{ color: '#CBD5E1', mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Switch size="small" checked={s.include !== false} onChange={e => updateSection(i, 'include', e.target.checked)} />
                    <TextField size="small" value={s.title} onChange={e => updateSection(i, 'title', e.target.value)} variant="standard"
                      sx={{ flex: 1, '& .MuiInput-root': { fontWeight: 600, fontSize: 15 } }} />
                    <Chip label={s.id} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                    <IconButton size="small" onClick={() => removeSection(i)} color="error"><Delete fontSize="small" /></IconButton>
                  </Box>
                  <TextField fullWidth multiline rows={2} size="small" placeholder="Section instruction..."
                    value={s.instruction || ''} onChange={e => updateSection(i, 'instruction', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: 12 } }} />
                </Box>
              </Paper>
            ))}
            <Button startIcon={<Add />} onClick={addSection} variant="outlined" fullWidth sx={{ mt: 1 }}>Add Section</Button>
          </Box>
        )}

        {/* Diagrams */}
        {tab === 1 && (
          <Box>
            {diagrams.map((d, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 2.5, mb: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center' }}>
                  <TextField size="small" label="Title" value={d.title || ''} onChange={e => updateDiagram(i, 'title', e.target.value)} sx={{ flex: 2 }} />
                  <Select size="small" value={d.section_id || ''} onChange={e => updateDiagram(i, 'section_id', e.target.value)} sx={{ flex: 1.5 }}>
                    {sections.map(s => <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>)}
                  </Select>
                  <Select size="small" value={d.diagram_type || 'architecture'} onChange={e => updateDiagram(i, 'diagram_type', e.target.value)} sx={{ flex: 1.5 }}>
                    {['architecture', 'process_flow', 'data_flow', 'timeline', 'interaction'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                  <IconButton onClick={() => removeDiagram(i)} color="error"><Delete /></IconButton>
                </Box>
                <TextField fullWidth size="small" label="Description" value={d.description || ''} onChange={e => updateDiagram(i, 'description', e.target.value)} />
              </Paper>
            ))}
            <Button startIcon={<Add />} onClick={addDiagram} variant="outlined" fullWidth color="secondary" sx={{ mt: 1 }}>Add Diagram</Button>
          </Box>
        )}

        {/* Rules */}
        {tab === 2 && (
          <TextField fullWidth multiline rows={18} value={rules.join('\n')} placeholder="One rule per line..."
            onChange={e => setConfig({ ...config, generation_rules: e.target.value.split('\n').filter(r => r.trim()) })}
            sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'monospace', fontSize: 13 } }} />
        )}

        {/* Cost */}
        {tab === 3 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth label="Currency" value={cc.currency || ''} onChange={e => setConfig({ ...config, cost_estimation_config: { ...cc, currency: e.target.value } })} />
              <TextField fullWidth label="Market Context" value={cc.market_context || ''} onChange={e => setConfig({ ...config, cost_estimation_config: { ...cc, market_context: e.target.value } })} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField type="number" label="TCO Years" value={cc.tco_years || 5} onChange={e => setConfig({ ...config, cost_estimation_config: { ...cc, tco_years: parseInt(e.target.value) } })} sx={{ width: 150 }} />
              <FormControlLabel control={<Checkbox checked={cc.include_tco !== false} onChange={e => setConfig({ ...config, cost_estimation_config: { ...cc, include_tco: e.target.checked } })} />} label="Include TCO" />
              <FormControlLabel control={<Checkbox checked={cc.include_roi !== false} onChange={e => setConfig({ ...config, cost_estimation_config: { ...cc, include_roi: e.target.checked } })} />} label="Include ROI" />
            </Box>
          </Box>
        )}

        {/* JSON */}
        {tab === 4 && (
          <TextField fullWidth multiline rows={22} value={JSON.stringify(config, null, 2)}
            onChange={e => { try { setConfig(JSON.parse(e.target.value)); } catch {} }}
            sx={{ '& .MuiOutlinedInput-root': { fontFamily: 'monospace', fontSize: 11 } }} />
        )}
      </Box>

      <Box sx={{ p: 3, borderTop: '1px solid #E2E8F0', display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={onBack}>← Back</Button>
        <Button variant="outlined" startIcon={<Refresh />} color="warning"
          onClick={async () => {
            try {
              const result = await proposalService.analyze(session.sessionId);
              setConfig(result.config);
              setSession(s => ({ ...s, analysis: result }));
            } catch (err) { alert('Failed: ' + err.message); }
          }}>
          Regenerate Config
        </Button>
        <Button variant="contained" fullWidth onClick={() => { setSession(s => ({ ...s, config })); onNext(); }}
          sx={{ bgcolor: '#6D28D9', '&:hover': { bgcolor: '#5B21B6' } }}>
          Approve & Generate Proposal →
        </Button>
      </Box>
    </Paper>
  );
}
